import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  IconButton,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import Modal from 'components/_layouts/Modal';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import { QuickSearch } from 'components/_atoms';
import {
  Delete as DeleteIcon,
  Description as FileIcon,
  Image as ImageIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { translate as t } from 'hooks/useTranslations';
import useManageAttachment from '../../_data_sources/useManageAttachment';
import { AttachmentAddDialog } from './_components/AttachmentAddDialog';
import { DraftManualDetailContext } from '../context';
import { Attachment } from '../../../_types/manuals/draft';
import useManageManual from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManageManual';
import Loading from 'views/App/Portal/Loading';
import { enqueueSnackbar } from 'notistack';
import AttachmentEditDialog from './_components/AttachmentEditDialog';
import datetimeHelper from 'helpers/datetimeHelper';
import draftManualHelper from '../../helpers/draftManualHelper';
import { neutral } from '_lex/_foundations/phxColors';
import { isEmpty } from 'lodash';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';

interface AttachmentLibraryProps {
  open: boolean;
  onCancel: () => void;
  selectMode?: boolean;
  onSelectAttachments?: (selected: Attachment[]) => void;
}

interface AttachmentRow extends Attachment {}

const columnHelper = createColumnHelper<AttachmentRow>();

const AttachmentLibrary: React.FC<AttachmentLibraryProps> = ({
  open,
  onCancel,
  selectMode = false,
  onSelectAttachments,
}) => {
  const { getAttachment, deleteAttachment } = useManageAttachment();
  const { manual, manualId, manualQuery } = useContext(DraftManualDetailContext);
  const { getLock, removeLock } = useManageManual();

  const [attachments, setAttachments] = useState<AttachmentRow[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentlyDownloadingId, setCurrentlyDownloadingId] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [fileType, setFileType] = useState<string>('');
  const [attachmentToDelete, setAttachmentToDelete] = useState<AttachmentRow | null>(null);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [attachmentToEdit, setAttachmentToEdit] = useState<AttachmentRow | null>(null);
  const baseColumns = [
    columnHelper.accessor('name', {
      header: t('draftManuals.attachmentLibrary.columns.name'),
      size: 9999,
      cell: (info) => {
        const row = info.row.original;
        const fileExt = row.name.split('.').pop()?.toLowerCase() || '';
        let fileIcon = <FileIcon fontSize='small' />;
        if (['png', 'jpg', 'jpeg'].includes(fileExt)) {
          fileIcon = <ImageIcon fontSize='small' />;
        }
        if (currentlyDownloadingId === row.id) {
          return (
            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
              sx={{ width: 6, height: 6 }}
            >
              {fileIcon}
              <Loading />
            </Stack>
          );
        }

        const handleDownload = async () => {
          try {
            if (!manualId) return;
            setCurrentlyDownloadingId(row.id);
            const response = await getAttachment({ manualId, attachmentId: row.id });
            if (response && response.url) {
              const fetchResp = await fetch(response.url, { method: 'GET' });
              const blob = await fetchResp.blob();
              const fileName = response.name || 'download';
              const href = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = href;
              link.setAttribute('download', fileName);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(href);
              setCurrentlyDownloadingId(null);
            }
          } catch (error) {
            console.error('Failed to download attachment', error);
          } finally {
            setCurrentlyDownloadingId(null);
          }
        };

        return (
          <Stack
            direction='row'
            spacing={1}
            alignItems='center'
          >
            {fileIcon}
            <Typography
              variant='body2'
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
              onClick={handleDownload}
            >
              {row.name}
            </Typography>
          </Stack>
        );
      },
    }),
    columnHelper.accessor('size', {
      header: t('draftManuals.attachmentLibrary.columns.size'),
      cell: (info) => {
        const sizeVal = parseInt(info.getValue(), 10) || 0;
        const kb = (sizeVal / 1024).toFixed(1) + ' KB';
        return <Typography variant='body2'>{kb}</Typography>;
      },
    }),
    columnHelper.accessor('contentReferences', {
      header: t('draftManuals.attachmentLibrary.columns.location'),
      cell: (info) => (
        <Stack>
          {info.row.original.contentReferences.length > 0 ? (
            info.row.original.contentReferences.map((ref) => (
              <Typography
                key={ref.contentUri}
                variant='body1'
                sx={{
                  maxWidth: 360,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {draftManualHelper.getTitleByContentUri(ref.contentUri, manual)}
              </Typography>
            ))
          ) : (
            <Typography
              variant='body1'
              sx={{ color: neutral[400] }}
            >
              None
            </Typography>
          )}
        </Stack>
      ),
    }),
    columnHelper.display({
      id: 'uploadAt',
      header: t('draftManuals.attachmentLibrary.columns.uploadAt'),
      cell: (info) => {
        const { uploadAt } = info.row.original;
        return uploadAt ? datetimeHelper.formatDate(uploadAt) : 'Unknown';
      },
    }),
    columnHelper.display({
      id: 'uploadedBy',
      header: t('draftManuals.attachmentLibrary.columns.uploadedBy'),
      cell: (info) => {
        const { firstName, lastName, jurisdictionAttachmentId, globalAttachmentId } = info.row.original;

        if (manual?.manualLevel === 'AGENCY' && (jurisdictionAttachmentId || globalAttachmentId)) {
          return 'Lexipol';
        }

        const fullName = [firstName, lastName].join(' ').trim();
        return isEmpty(fullName) ? 'User Removed' : fullName;
      },
    }),

    columnHelper.display({
      id: 'actions',
      header: '',
      cell: (info) => {
        const row = info.row.original;
        return (
          <Stack
            direction='row'
            spacing={2}
          >
            <Tooltip title='Edit'>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  setAttachmentToEdit(row);
                }}
              >
                <EditIcon fontSize='small' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Delete'>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  setAttachmentToDelete(row);
                }}
              >
                <DeleteIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
      size: 15,
    }),
  ];

  const filteredAttachments = useMemo(() => {
    return attachments.filter((att) => {
      const nameMatch = att.name?.toLowerCase().includes(searchTerm.toLowerCase());
      const ext = att.name?.split('.').pop()?.toLowerCase() || '';
      const typeMatch = fileType ? ext === fileType : true;
      return nameMatch && typeMatch;
    });
  }, [attachments, searchTerm, fileType]);

  const computedColumns = useMemo(() => {
    return {
      name: {
        header: t('draftManuals.attachmentLibrary.columns.name'),
        flex: 1,
        renderer: (params: any) => {
          const row = params.row;
          const fileExt = row.name.split('.').pop()?.toLowerCase() || '';
          let fileIcon = <FileIcon fontSize='small' />;
          if (['png', 'jpg', 'jpeg'].includes(fileExt)) {
            fileIcon = <ImageIcon fontSize='small' />;
          }
          if (currentlyDownloadingId === row.id) {
            return (
              <Stack
                direction='row'
                spacing={1}
                alignItems='center'
                sx={{ width: 6, height: 6 }}
              >
                {fileIcon}
                <Loading />
              </Stack>
            );
          }

          const handleDownload = async () => {
            try {
              if (!manualId) return;
              setCurrentlyDownloadingId(row.id);
              const response = await getAttachment({ manualId, attachmentId: row.id });
              if (response && response.url) {
                const fetchResp = await fetch(response.url, { method: 'GET' });
                const blob = await fetchResp.blob();
                const fileName = response.name || 'download';
                const href = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = href;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(href);
                setCurrentlyDownloadingId(null);
              }
            } catch (error) {
              console.error('Failed to download attachment', error);
            } finally {
              setCurrentlyDownloadingId(null);
            }
          };

          return (
            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
            >
              {fileIcon}
              <Typography
                variant='body2'
                sx={{
                  fontWeight: 600,
                  color: 'primary.main',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
                onClick={handleDownload}
              >
                {row.name}
              </Typography>
            </Stack>
          );
        },
      },
      size: {
        header: t('draftManuals.attachmentLibrary.columns.size'),
        renderer: (params: any) => {
          const sizeVal = parseInt(params.value, 10) || 0;
          const kb = (sizeVal / 1024).toFixed(1) + ' KB';
          return <Typography variant='body2'>{kb}</Typography>;
        },
      },
      contentReferences: {
        header: t('draftManuals.attachmentLibrary.columns.location'),
        flex: 1,
        renderer: (params: any) => (
          <Stack>
            {params.row.contentReferences.length > 0 ? (
              params.row.contentReferences.map((ref: any) => (
                <Typography
                  key={ref.contentUri}
                  variant='body1'
                  sx={{
                    maxWidth: 360,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {draftManualHelper.getTitleByContentUri(ref.contentUri, manual)}
                </Typography>
              ))
            ) : (
              <Typography
                variant='body1'
                sx={{ color: neutral[400] }}
              >
                None
              </Typography>
            )}
          </Stack>
        ),
      },
      uploadAt: {
        header: t('draftManuals.attachmentLibrary.columns.uploadAt'),
        renderer: (params: any) => {
          const { uploadAt } = params.row;
          return uploadAt ? datetimeHelper.formatDate(uploadAt) : 'Unknown';
        },
      },
      uploadedBy: {
        header: t('draftManuals.attachmentLibrary.columns.uploadedBy'),
        renderer: (params: any) => {
          const { firstName, lastName, jurisdictionAttachmentId, globalAttachmentId } = params.row;

          if (manual?.manualLevel === 'AGENCY' && (jurisdictionAttachmentId || globalAttachmentId)) {
            return 'Lexipol';
          }

          const fullName = [firstName, lastName].join(' ').trim();
          return isEmpty(fullName) ? 'User Removed' : fullName;
        },
      },
      actions: {
        header: '',
        flex: 'auto',
        renderer: (params: any) => {
          const row = params.row;
          return (
            <Stack
              direction='row'
              spacing={2}
            >
              <Tooltip title='Edit'>
                <IconButton
                  size='small'
                  onClick={(e) => {
                    e.stopPropagation();
                    setAttachmentToEdit(row);
                  }}
                >
                  <EditIcon fontSize='small' />
                </IconButton>
              </Tooltip>
              <Tooltip title='Delete'>
                <IconButton
                  size='small'
                  onClick={(e) => {
                    e.stopPropagation();
                    setAttachmentToDelete(row);
                  }}
                >
                  <DeleteIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    };
  }, [currentlyDownloadingId, manual, manualId, t]);

  useEffect(() => {
    if (!open) return;
    const manualAttachments = manual?.attachments ?? [];
    setAttachments(manualAttachments);
  }, [open, manual]);

  const handleAddAttachment = () => {
    setAddDialogOpen(true);
  };

  const handleAddDialogClose = (didAdd: boolean, attachmentId?: string) => {
    setAddDialogOpen(false);
    if (didAdd && manualQuery) {
      manualQuery.refetch().then(() => {
        if (attachmentId) {
          setRowSelectionModel([attachmentId]);
        }
      });
      enqueueSnackbar(t('draftManuals.attachmentLibrary.addedAttachment'), {
        variant: 'success',
        autoHideDuration: 6000,
      });
    }
  };
  const uniqueFileTypes = useMemo(() => {
    const extensions = new Set<string>();
    attachments.forEach((att) => {
      const ext = att.name.split('.').pop()?.toLowerCase();
      if (ext) extensions.add(ext);
    });
    return Array.from(extensions).sort();
  }, [attachments]);

  const handleConfirmDelete = async () => {
    if (!attachmentToDelete || !manualId) return;
    setIsDeleting(true);
    try {
      await new Promise<void>((resolve, reject) => {
        getLock(
          manualId,
          () => resolve(),
          (err) => reject(err)
        );
      });

      await new Promise<void>((resolve, reject) => {
        deleteAttachment(
          manualId,
          attachmentToDelete.id,
          () => resolve(),
          (err) => reject(err)
        );
      });

      await new Promise<void>((resolve, reject) => {
        removeLock(
          manualId,
          () => resolve(),
          (err) => reject(err)
        );
      });

      if (manualQuery) {
        await manualQuery.refetch();
      }
      setAttachmentToDelete(null);
      enqueueSnackbar(t('draftManuals.attachmentLibrary.deletedAttachment'), {
        variant: 'success',
        autoHideDuration: 6000,
      });
    } catch (err) {
      console.error('Error locking or deleting attachment:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onCancel}
        title={selectMode ? 'Select from Manual Library' : 'Manage Library'}
        size='xl'
        actions={
          selectMode ? (
            <>
              <Button
                color='neutral'
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                variant='contained'
                disabled={rowSelectionModel.length === 0}
                onClick={() => {
                  if (onSelectAttachments) {
                    const selectedAttachments = attachments.filter((att) => rowSelectionModel.includes(att.id));
                    onSelectAttachments(selectedAttachments);
                  }
                  onCancel();
                }}
              >
                Insert
              </Button>
            </>
          ) : undefined
        }
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateRows: 'auto auto 1fr',
            gap: 2,
            py: 3,
            minHeight: 0,
            maxHeight: '55vh',
          }}
        >
          <Box sx={{ mb: 1 }}>
            <Typography
              variant='subtitle1'
              sx={{ fontSize: 20 }}
            >
              {`${manual.name} files`}
            </Typography>
          </Box>

          <Stack
            direction='row'
            spacing={2}
            alignItems='center'
          >
            <QuickSearch
              id='attachment_search'
              initialValue={searchTerm}
              onChange={(val) => setSearchTerm(val)}
              placeholder='Search by file name'
              minWidth={250}
            />
            <Select
              size='small'
              displayEmpty
              value={fileType}
              onChange={(e) => setFileType(e.target.value)}
              sx={{ minWidth: 150, height: 36 }}
            >
              <MenuItem value=''>All</MenuItem>
              <ListSubheader>Filter by:</ListSubheader>
              {uniqueFileTypes.map((type) => (
                <MenuItem
                  key={type}
                  value={type}
                >
                  {type.toUpperCase()}
                </MenuItem>
              ))}
            </Select>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant='contained'
              size='small'
              onClick={handleAddAttachment}
            >
              Upload File
            </Button>
          </Stack>

          <GridSection
            data={filteredAttachments}
            columns={computedColumns}
            query={{ isLoading: isFetching || isDeleting, error: undefined }}
            totalCount={filteredAttachments.length}
            gridProps={{
              autoHeight: false,
              rowHeight: 64,
              columnHeaderHeight: 40,
              disableColumnMenu: true,
              disableColumnSorting: true,
              hideFooterPagination: true,
              checkboxSelection: selectMode,
              onRowSelectionModelChange: (newRowSelectionModel: GridRowSelectionModel) => {
                setRowSelectionModel(newRowSelectionModel);
              },
              rowSelectionModel,
            }}
          />
        </Box>
      </Modal>
      {
        <AttachmentAddDialog
          open={addDialogOpen}
          onClose={handleAddDialogClose}
        />
      }

      {attachmentToEdit && (
        <AttachmentEditDialog
          open={!!attachmentToEdit}
          manualId={manualId!}
          attachment={attachmentToEdit}
          onClose={() => setAttachmentToEdit(null)}
          onUpdated={() => {
            if (manualQuery) manualQuery.refetch();
          }}
        />
      )}
      {attachmentToDelete && (
        <Modal
          open={!!attachmentToDelete}
          onClose={() => setAttachmentToDelete(null)}
          title='Confirm Delete'
          size='xs'
        >
          {attachmentToDelete.contentReferences && attachmentToDelete.contentReferences.length > 0 ? (
            <>
              <Typography>This attachment is currently in use and cannot be deleted.</Typography>
              <DialogActions>
                <Button onClick={() => setAttachmentToDelete(null)}>Close</Button>
              </DialogActions>
            </>
          ) : (
            <>
              <Typography>Are you sure you want to remove {attachmentToDelete.name} from this manual?</Typography>
              <DialogActions>
                <Button onClick={() => setAttachmentToDelete(null)}>Cancel</Button>
                <Button
                  variant='contained'
                  color='error'
                  onClick={handleConfirmDelete}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </Button>
              </DialogActions>
            </>
          )}
        </Modal>
      )}
    </>
  );
};

export default AttachmentLibrary;
